import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { searchCpv, findCpvLabel, cacheCpvLabels } from "../data/cpv_codes";
import { api } from "../lib/api";

/**
 * Searchable CPV combobox.
 *  - mode="multi": value is string[]; add chips, remove on chip click
 *  - mode="single": value is string; clears on selection of another / X
 *
 * Accepts free 8-digit codes the user types (not in curated list) and adds them.
 */
export default function CpvCombobox({
  mode = "single",
  value,
  onChange,
  placeholder = "Search CPV code or description…",
  "data-testid": testId = "cpv-combobox",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  const selected = mode === "multi" ? (value || []) : value ? [value] : [];

  // Local fallback results (curated defence-relevant subset, used until server replies).
  const localResults = useMemo(
    () => searchCpv(query, { limit: 30, exclude: mode === "multi" ? selected : [] }),
    [query, selected.join(","), mode],
  );

  // Server-side results (full ~9,454 EU CPV vocabulary). Debounced.
  const [serverResults, setServerResults] = useState(null); // null = not loaded yet
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/cpv/search", { params: { q: query, limit: 30 } });
        cacheCpvLabels(data);
        setServerResults(data);
      } catch (e) {
        // Network fail — keep curated fallback visible.
        setServerResults(null);
      }
    }, 150);
    return () => clearTimeout(handle);
  }, [query]);

  // Prefer server results when we have them, otherwise show local curated.
  const exclude = mode === "multi" ? selected : [];
  const excludeSet = new Set(exclude);
  const results = (serverResults || localResults).filter((r) => !excludeSet.has(r.code)).slice(0, 30);

  // Allow free-form 8-digit code if not in curated list
  const numericQ = query.replace(/\D/g, "").slice(0, 8);
  const showFreeOption =
    numericQ.length >= 2 &&
    !results.some((r) => r.code.startsWith(numericQ)) &&
    !selected.includes(numericQ);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (code) => {
    if (mode === "multi") {
      onChange([...(value || []), code]);
      setQuery("");
    } else {
      onChange(code);
      setQuery("");
      setOpen(false);
    }
  };

  const removeChip = (code) => {
    if (mode === "multi") {
      onChange((value || []).filter((c) => c !== code));
    } else if (value === code) {
      onChange("");
    }
  };

  const clearAll = () => {
    if (mode === "multi") onChange([]);
    else onChange("");
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }} data-testid={testId}>
      {/* Input — shaped to match sibling <select> elements when used as a
          single-mode filter: no leading icon when empty, selected value shown
          inline inside the input, chevron on the right. */}
      <div className="relative">
        {(mode === "multi" || query) && (
          <Search
            size={14}
            style={{ position: "absolute", left: 10, top: 11, color: "var(--parapet-text-secondary)" }}
          />
        )}
        <input
          className="p-input w-full"
          style={{
            paddingLeft: (mode === "multi" || query) ? 32 : 12,
            paddingRight: 30,
            // Slightly muted look when displaying the selected single value
            // so it reads as "filter applied" not "placeholder".
            color: mode === "single" && value && !query ? "#111" : undefined,
          }}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (results[0] || (showFreeOption && numericQ.length === 8))) {
              e.preventDefault();
              pick(results[0]?.code || numericQ);
            }
          }}
          placeholder={
            mode === "single" && value
              ? `${value}${findCpvLabel(value) ? " — " + truncate(findCpvLabel(value), 28) : ""}`
              : placeholder
          }
          data-testid={`${testId}-input`}
        />
        {mode === "single" && value && !query ? (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear CPV filter"
            style={{
              position: "absolute", right: 8, top: 8, padding: 2,
              background: "transparent", border: 0, cursor: "pointer",
              color: "var(--parapet-text-secondary)",
            }}
            data-testid={`${testId}-clear-single`}
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown
            size={14}
            onClick={() => setOpen((v) => !v)}
            style={{
              position: "absolute",
              right: 10,
              top: 11,
              cursor: "pointer",
              color: "var(--parapet-text-secondary)",
            }}
          />
        )}
      </div>

      {/* Selected chips (multi only — single-mode shows the value inline above) */}
      {mode === "multi" && selected.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap" data-testid={`${testId}-chips`}>
          {selected.map((c) => (
            <span
              key={c}
              className="filter-chip active"
              onClick={() => removeChip(c)}
              title={findCpvLabel(c) || "custom code"}
              data-testid={`${testId}-chip-${c}`}
              style={{ cursor: "pointer" }}
            >
              <span className="value-mono">{c}</span>
              {findCpvLabel(c) && (
                <span style={{ opacity: 0.7, marginLeft: 6, fontSize: 11 }}>
                  {truncate(findCpvLabel(c), 32)}
                </span>
              )}
              <X size={11} style={{ marginLeft: 4 }} />
            </span>
          ))}
          <button
            type="button"
            className="text-[11px] underline"
            onClick={clearAll}
            data-testid={`${testId}-clear`}
            style={{ color: "var(--parapet-text-secondary)" }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Dropdown */}
      {open && (results.length > 0 || showFreeOption) && (
        <div
          className="p-card"
          style={{
            position: "absolute",
            zIndex: 50,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            padding: 0,
            maxHeight: 320,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid var(--parapet-border)",
            boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
          }}
          data-testid={`${testId}-options`}
        >
          {results.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => pick(r.code)}
              className="w-full text-left hover:bg-gray-50 flex items-baseline gap-3"
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid var(--parapet-grid)",
                lineHeight: 1.35,
              }}
              data-testid={`${testId}-option-${r.code}`}
            >
              <span className="value-mono text-[12px]" style={{ minWidth: 84, color: "#111" }}>
                {r.code}
              </span>
              <span className="text-[13px]" style={{ color: "#111" }}>
                {r.label}
              </span>
            </button>
          ))}
          {showFreeOption && (
            <button
              type="button"
              onClick={() => pick(numericQ)}
              className="w-full text-left hover:bg-gray-50 flex items-baseline gap-3"
              style={{ padding: "10px 14px", background: "#FAFAFA" }}
              data-testid={`${testId}-option-custom`}
            >
              <span className="value-mono text-[12px]" style={{ minWidth: 84 }}>
                {numericQ.padEnd(8, "·")}
              </span>
              <span className="text-[13px]" style={{ color: "var(--parapet-text-secondary)" }}>
                Add custom code <span className="value-mono">"{numericQ}"</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
